import type { ReactNode } from 'react'

import { Spinner, Text } from '@chakra-ui/react'
import { Menu } from '@saas-ui/react'

import { AlertTriangleIcon } from '#components/icons/alert-triangle-icon.tsx'

import type { getWorkspaceConnectionStatus } from './workspace-connection-status'

type WorkspaceMenuProps = {
  label: string | undefined
  logo: ReactNode
  connection: ReturnType<typeof getWorkspaceConnectionStatus>
  retrying: boolean
  onRetry: () => void
  onSignIn: () => void
  onReload: () => void
  children: ReactNode
}

/** Shared by the live workspace menu and its interactive Storybook examples. */
export function WorkspaceMenu({
  label,
  logo,
  connection,
  retrying,
  onRetry,
  onSignIn,
  onReload,
  children,
}: WorkspaceMenuProps) {
  const reconnecting = retrying || connection?.reconnecting === true
  const triggerLabel = reconnecting ? 'Reconnecting' : connection?.label

  return (
    <Menu.Root>
      <Menu.Button
        aria-label={`${triggerLabel ? `${triggerLabel}. ` : ''}Current workspace is ${label}. Open workspace menu`}
        aria-busy={reconnecting}
        variant={connection ? 'subtle' : 'ghost'}
        colorPalette={connection ? 'orange' : undefined}
        borderRadius={connection ? 'full' : undefined}
        size="sm"
        minW="0"
        maxW="full"
        _hover={{ bg: connection ? 'colorPalette.muted' : 'sidebar.accent.bg' }}
        _expanded={{
          bg: connection ? 'colorPalette.muted' : 'sidebar.accent.bg',
        }}
      >
        {reconnecting ? (
          <Spinner size="xs" aria-hidden="true" />
        ) : connection ? (
          <AlertTriangleIcon aria-hidden="true" />
        ) : (
          logo
        )}
        <Text as="span" truncate>
          {triggerLabel ?? label}
        </Text>
      </Menu.Button>
      <Text srOnly role="status" aria-live="polite" aria-atomic="true">
        {reconnecting
          ? 'Reconnecting to Trevize.'
          : connection
            ? `${connection.label}. ${connection.description}`
            : ''}
      </Text>

      <Menu.Content minW="200px" portalled>
        {connection?.action && (
          <>
            {(connection.action === 'sync' ||
              connection.action === 'request') && (
              <Menu.Item
                value="reconnect"
                disabled={retrying}
                onClick={() => onRetry()}
              >
                {retrying ? 'Reconnecting…' : 'Try again'}
              </Menu.Item>
            )}
            {connection.action === 'sign-in' && (
              <Menu.Item value="sign-in" asChild>
                <button type="button" onClick={onSignIn}>
                  Sign in again
                </button>
              </Menu.Item>
            )}
            {connection.action === 'reload' && (
              <Menu.Item value="reload" onClick={onReload}>
                Reload Trevize
              </Menu.Item>
            )}
            <Menu.Separator />
          </>
        )}
        {children}
      </Menu.Content>
    </Menu.Root>
  )
}
